import React from 'react';
import { UploadingState } from './AudioToolsModels';
import { Evaporate, EvaporateConfigHelper } from '@revdotcom/evaporate';
import { TranscribeUploadingView } from './TranscribeUploadingView';
import { formatBytes, revWindow, Fetch } from '@revdotcom/common';
/**
 * Component that allows user to add audio to user's TC cart.
 */
export class TranscribeButton extends React.Component {
    constructor(props) {
        super(props);
        this.trimFileAndUploadToS3 = async () => {
            const recordedFile = await this.props.getTrimmedFile();
            if (!recordedFile || recordedFile.size === 0) {
                this.setState({
                    uploadingState: UploadingState.Error,
                });
                this.props.setMessage('Cannot transcribe an empty file. Please try again.');
                return;
            }
            this.fileSize = recordedFile.size;
            window.analytics.track('Button Click', {
                altEventName: `${this.props.segmentAudioToolName}: Transcribe`,
                stateChange: 'Redirect',
                locationUrl: revWindow.baseUrl(),
                redirectUrl: this.cartRedirectUrl,
                buttonText: 'Transcribe',
                buttonLocation: 'Main Page (after recording or file uploaded)',
            });
            if (!this.awsPath || !this.awsPath.endsWith('/')) {
                this.awsPath += '/';
            }
            const fileKey = `${this.awsPath}${recordedFile.name}`;
            this.evaporateConfigHelper.getEvaporateConfig(recordedFile).then((config) => {
                Evaporate.create(config).then((evaporate) => {
                    evaporate.add({
                        name: fileKey,
                        file: recordedFile,
                        started: this.uploadStarted,
                        progress: this.uploadProgress,
                        complete: this.uploadCompleted,
                        error: this.uploadFailed,
                        warn: this.uploadFailed,
                    });
                });
            });
        };
        this.handleTranscribeClick = () => {
            // Inform users their file is getting trimmed
            this.props.setMessage('Trimming your file...');
            this.setState({
                uploadingState: UploadingState.Uploading,
            }, async () => {
                try {
                    await this.trimFileAndUploadToS3();
                }
                catch (_a) {
                    this.props.setMessage(TranscribeButton.DefaultUploadFailureMessage);
                }
            });
        };
        this.uploadStarted = () => {
            this.setState({
                uploadingState: UploadingState.Uploading,
            });
            this.handleFileSizeMessaging(0);
        };
        this.uploadProgress = (progressValue) => {
            if (this.state.uploadingState === UploadingState.Uploading) {
                this.setState({
                    percentCompleted: progressValue,
                });
                this.handleFileSizeMessaging(progressValue);
            }
        };
        this.uploadCompleted = async (_, awsKey) => {
            const decodedAwsKey = decodeURIComponent(awsKey);
            this.props.setMessage('');
            this.setState({
                uploadingState: UploadingState.Uploaded,
            });
            await this.sendToCart(decodedAwsKey);
        };
        this.sendToCart = async (awsKey) => {
            try {
                await Fetch.json('/checkout/createtccartfromfile', 'POST', {
                    awsKey: awsKey,
                    fileDurationMinutes: this.props.getAudioLengthMinutes(),
                    isAutomatedTranscription: this.props.isAutomatedTranscription,
                });
                this.openTcCheckoutInNewTab();
                if (this.props.onUploadComplete) {
                    this.props.onUploadComplete();
                }
            }
            catch (e) {
                this.uploadFailed();
            }
        };
        this.openTcCheckoutInNewTab = () => {
            revWindow.open(this.cartRedirectUrl);
        };
        this.uploadFailed = (message) => {
            this.setState({
                uploadingState: UploadingState.Error,
            });
            this.props.setMessage(message ? message : TranscribeButton.DefaultUploadFailureMessage);
        };
        this.handleFileSizeMessaging = (progressValue) => {
            const totalSize = formatBytes(this.fileSize);
            const completedSize = formatBytes(Math.round(this.fileSize * progressValue));
            this.props.setMessage(completedSize + '/' + totalSize);
        };
        this.renderTranscribeButton = () => {
            switch (this.state.uploadingState) {
                case UploadingState.Uploading:
                    return React.createElement(TranscribeUploadingView, { percentCompleted: this.state.percentCompleted });
                case UploadingState.Error:
                    return (React.createElement("button", { id: "transcribe-button-error", className: "transcribe-button", onClick: this.handleTranscribeClick }, "TRY AGAIN"));
                case UploadingState.Uploaded:
                    return (React.createElement("button", { id: "transcribe-button-uploaded", className: "transcribe-button", onClick: this.openTcCheckoutInNewTab }, "GO TO CART"));
                default:
                    return (React.createElement("button", { id: "transcribe-button-ready", className: "transcribe-button", onClick: this.handleTranscribeClick }, "Transcribe"));
            }
        };
        this.state = {
            uploadingState: UploadingState.Ready,
            percentCompleted: 0,
        };
        this.fileSize = 0;
        this.awsPath = props.awsUploadInfo.path;
        this.cartRedirectUrl = props.isAutomatedTranscription
            ? '/checkout/automated-transcription-files'
            : '/checkout/transcriptionfiles';
        const baseEvaporateConfig = {
            signerUrl: this.props.awsUploadInfo.signerUrl,
            aws_key: this.props.awsUploadInfo.accessKeyId,
            bucket: this.props.awsUploadInfo.bucket,
            cloudfront: true,
            computeContentMd5: false,
            maxConcurrentParts: 100,
            awsSignatureVersion: 4,
            logging: false,
            signHeaders: { 'x-requested-with': 'XMLHttpRequest' },
        };
        this.evaporateConfigHelper = new EvaporateConfigHelper(baseEvaporateConfig);
    }
    render() {
        return React.createElement("div", { id: "transcribe-button-container" }, this.renderTranscribeButton());
    }
}
TranscribeButton.DefaultUploadFailureMessage = "We're sorry, we were unable to add your recording to your cart. Please try again.";
